// src/components/GardenMap.tsx

import React, { useEffect } from 'react';
import Map, { Source, Layer, Marker, useControl, ControlPosition, MapMouseEvent } from 'react-map-gl/mapbox';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Box, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import type { Feature, Polygon, FeatureCollection } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

interface PolygonProperties {
    uid?: string;
    name?: string;
    is_owner?: boolean;
    is_uncertain?: boolean;
    centroid?: { type: 'Point'; coordinates: number[] };
}
type PolygonFeature = Feature<Polygon, PolygonProperties>;

// --- DRAW CONTROL ---
const customDrawStyles = [
  { id: 'gl-draw-polygon-fill-active', type: 'fill', paint: { 'fill-color': '#fbb03b', 'fill-opacity': 0.2 } },
  { id: 'gl-draw-polygon-stroke-active', type: 'line', paint: { 'line-color': '#fbb03b', 'line-width': 3 } },
  { id: 'gl-draw-point-active', type: 'circle', paint: { 'circle-radius': 7, 'circle-color': '#fbb03b' } },
  // Make intermediate points less prominent
  { id: 'gl-draw-polygon-midpoint', type: 'circle', paint: { 'circle-radius': 3, 'circle-color': '#fbb03b' } },
];

interface DrawControlProps {
    position?: ControlPosition;
    onCreate: (evt: { features: PolygonFeature[] }) => void;
    onUpdate: (evt: { features: PolygonFeature[]; action:string }) => void;
    onActionable: (evt: any) => void;
    drawRef: React.MutableRefObject<MapboxDraw | null>;
    isDrawing?: boolean;
}

function DrawControl({ position, onCreate, onUpdate, onActionable, drawRef, isDrawing }: DrawControlProps) {
    useControl<MapboxDraw>(
        () => {
            const draw = new MapboxDraw({ 
                styles: customDrawStyles, 
                displayControlsDefault: false,
                controls: {
                    polygon: false,
                    trash: false,
                    combine_features: false,
                    uncombine_features: false
                },
                // Significantly increase click buffer to reduce accidental vertex selection
                clickBuffer: 2,  // Keep it small for precision
                touchBuffer: 25   // Larger for touch devices
            });
            drawRef.current = draw;
            return draw;
        },
        ({ map }) => {
            map.on('draw.create', onCreate as any);
            map.on('draw.update', onUpdate as any);
            map.on('draw.actionable', onActionable as any);
            
            // Handle mode changes to provide feedback
            map.on('draw.modechange', (e: any) => {
                // Mode change tracking could be added here if needed
            });
        },
        ({ map }) => {
            map.off('draw.create', onCreate as any);
            map.off('draw.update', onUpdate as any);
            map.off('draw.actionable', onActionable as any);
            map.off('draw.modechange', () => {});
        },
        { position }
    );
    
    // Effect to handle drawing state changes
    useEffect(() => {
        if (drawRef.current && isDrawing) {
            // Start drawing mode
            drawRef.current.changeMode('draw_polygon');
        }
    }, [isDrawing, drawRef]);
    
    return null;
}


// --- PROPS & COMPONENT ---
interface GardenMapProps {
    viewState: { longitude: number; latitude: number; zoom: number };
    pubName: string;
    features: PolygonFeature[];
    drawRef: React.MutableRefObject<MapboxDraw | null>;
    onDrawCreate: (feature: PolygonFeature) => void;
    onDrawUpdate: (feature: PolygonFeature) => void;
    onActionable: (evt: any) => void;
    onMapClick: (featureId: string) => void;
    isDrawing?: boolean;  // Add this prop to track drawing state
}

export const GardenMap: React.FC<GardenMapProps> = ({
    viewState, pubName, features, drawRef, onDrawCreate, onDrawUpdate, onActionable, onMapClick, isDrawing
}) => {
    const handleMapClick = (event: MapMouseEvent) => {
        // Only handle feature clicks when not drawing
        if (!isDrawing) {
            const polygonLayerFeature = event.features?.find(
                (f: MapboxGeoJSONFeature) => f.layer?.id === 'polygon-fill'
            );
            if (polygonLayerFeature?.id) {
                onMapClick(String(polygonLayerFeature.id));
            }
        }
    };

    const featureCollection: FeatureCollection<Polygon, PolygonProperties> = { type: 'FeatureCollection', features };
    const labelCollection: FeatureCollection = {
        type: 'FeatureCollection',
        features: features
            .filter(f => f.properties?.centroid)
            .map(f => ({
                type: 'Feature',
                geometry: f.properties!.centroid!,
                properties: { name: f.properties!.name, is_uncertain: f.properties!.is_uncertain }
            })),
    };

    return (
        <Map
            initialViewState={viewState}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            mapboxAccessToken={MAPBOX_TOKEN}
            onClick={handleMapClick}
            interactiveLayerIds={isDrawing ? [] : ['polygon-fill']}  // Disable layer interaction when drawing
            cursor={isDrawing ? 'crosshair' : 'auto'}  // Show crosshair cursor when drawing
        >
            <DrawControl
                position="top-left"
                onCreate={(e) => onDrawCreate(e.features[0])}
                onUpdate={(e) => onDrawUpdate(e.features[0])}
                onActionable={onActionable}
                drawRef={drawRef}
                isDrawing={isDrawing}
            />
            <Marker longitude={viewState.longitude} latitude={viewState.latitude} anchor="bottom">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {pubName}
                    </Typography>
                    <LocationOnIcon color="error" fontSize="large" />
                </Box>
            </Marker>
            <Source id="my-polygons" type="geojson" data={featureCollection}>
                <Layer id="polygon-fill" type="fill" paint={{ 'fill-color': '#0070f3', 'fill-opacity': 0.5 }} />
                <Layer id="polygon-outline" type="line" paint={{ 'line-color': '#0070f3', 'line-width': 2 }} />
            </Source>
            <Source id="polygon-labels" type="geojson" data={labelCollection}>
                <Layer
                    id="label-layer"
                    type="symbol"
                    layout={{
                        'text-field': ['concat', ['get', 'name'], ['case', ['get', 'is_uncertain'], ' ?', '']],
                        'text-size': 16,
                        'text-anchor': 'top',
                        'text-allow-overlap': false
                    }}
                    paint={{ 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1.5 }}
                />
            </Source>
        </Map>
    );
};